<template>
  <div v-if="options">
    <assignment-dialog
      :model="dialogs.assign"
      :coverage="coverage"
      :assignment="item"
      @close="dialogs.assign = false"
      @success="onSuccess"
      :plan="plan"
      :brokerId="brokerId"
    />
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="800px">
      <material-card
        :title="$t('components.guides.edit_plan_fee_guide_province')"
        icon="edit"
        :color="$vuetify.theme.themes.light.success"
      >
        <v-card-text v-if="dialogs.edit">
          <v-form v-model="valid">
            <v-row>
              <v-col>
                <trans-enum
                  :value.sync="plan.ClaimSettings.FeeGuideProvince"
                  :items="options.Enums.FeeGuideProvince"
                  :label="
                    $t('headers.plan_guides_assignments.fee_guide_province')
                  "
                  :rules="[$rules.required]"
                  class="required"
                ></trans-enum>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="saving" text @click="dialogs.edit = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            :disabled="!valid"
            color="primary"
            :loading="saving"
            @click="update"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="item"
        :title="$t('components.guides.delete_plan_fee_guide_{0}', [guideTitle2()])"
        :color="'#00a8d7'"
        icon="delete"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn :disabled="saving" color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn :loading="saving" color="error" text @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <material-card
      :title="$t('components.guides.assignments_{0}', [guideTitle()])"
      :color="$vuetify.theme.themes.light.primary"
      icon="architecture"
    >
      <pbas-data-table
        :exporters="$exportersWithHistory"
        ref="table"
        :dataProvider="provider"
        :floatActions="floatActions"
        :rowActions="rowActions"
        fileNamePrefix="planGuidesAssignments"
        identifier="planGuidesAssignmentsTable"
        :canExpand="() => true"
        :showExpand="true"
        :generateProviderForExpanded="(item) => generateProvider(item)"
        @error="error = $event"
        :expandedFloatActions="(item) => expandedFloatActions(item)"
        :expandedRowActions="expandedRowActions"
      ></pbas-data-table>
    </material-card>
    <v-snackbar v-model="snackbar" color="success" :timeout="2500">
      {{ $t("globals.success") }}
      <v-btn dark text @click="snackbar = false">
        {{ $t("globals.close") }}
      </v-btn>
    </v-snackbar>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import { ODataProvider as PbasOData } from "pbas-vue-components";
import getHeaders from "@/headers/PlanGuidesAssignments";
import { locker } from "@/mixins/locker";
import { translate2 } from "@/trans2";
import getExpandableHeaders from "@/headers/PlanFeeGuides";
import AssignmentDialog from "./AssignmentDialog";
import TransEnum from "../TransEnum.vue";

export default {
  props: ["coverage", "brokerId"],
  components: {
    ErrorHandler,
    AssignmentDialog,
    TransEnum,
  },
  mixins: [locker],
  data() {
    return {
      loading: false,
      snackbar: false,
      valid: false,
      provider: undefined,
      saving: false,
      floatActions: [
        {
          text: "globals.add",
          color: "#64ba5f",
          name: "add",
          icon: "add",
          handler: () => {
            this.plan = null;
            this.dialogs.assign = true;
          },
        },
      ],
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.plan = JSON.parse(JSON.stringify(item));
            if (!this.plan.ClaimSettings) {
              this.plan.ClaimSettings = {};
            }
            this.dialogs.edit = true;
          },
        },
      ],
      expandedRowActions: [
        // {
        //     name: "edit",
        //     icon: "edit",
        //     title: "globals.edit",
        //     handler: (item) => {
        //       this.item = item;
        //       this.dialogs.assign = true;
        //     },
        //   },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          handler: (item) => {
            this.item = item;
            this.dialogs.delete = true;
          },
        },
      ],
      dialogs: {
        edit: false,
        assign: false,
        delete: false,
      },
      error: null,
      plan: null,
      item: null,
    };
  },
  computed: {
    ...mapGetters("common", ["language"]),
    ...mapGetters("plansMembers", ["options"]),
    formTitle() {
      let name = "";
      if (this.taxType) {
        name = this.$t("enums." + this.taxType);
      }
      return this.$t("views.taxes.edit_{0}", [name]);
    },
  },
  mounted() {
    if (this.options) {
      this.provider = new PbasOData(
        this.$axiosHttp,
        `plans-depending-fee-guide/${this.brokerId}/true/coverage/${this.coverage}`,
        getHeaders(this.options.Enums)
      );
    }
  },
  watch: {
    options: {
      handler(v) {
        if (v) {
          this.provider = new PbasOData(
            this.$axiosHttp,
            `plans-depending-fee-guide/${this.brokerId}/true/coverage/${this.coverage}`,
            getHeaders(this.options.Enums)
          );
        }
      },
    },
  },
  methods: {
    ...mapActions("claims", [
      "updatePlanFeeGuideProvince",
      "deletePlanFeeGuide",
    ]),
    guideTitle(){
      if (this.coverage==="Health") {
        return this.$t("views.guides.health_reasonable_&_customary_guides");
      }
      return this.$t("views.guides.dental_fee_guides");
    },
    guideTitle2(){
      if (this.coverage==="Health") {
        return this.$t("views.guides.health_reasonable_&_customary_guides_2");
      }
      return this.$t("views.guides.dental_fee_guides");
    },
    generateProvider(item) {
      let url = "plans/" + item.PlanId + "/plan-fee-guides";
      let p = new PbasOData(
        this.$axiosHttp,
        url,
        getExpandableHeaders(this.options.Enums, this.options.Models.Province)
      );
      p.filter=`FeeGuide/Coverage eq '${this.coverage}'`
      return p;
    },
    expandedFloatActions(item) {
      return [
        {
          text: "globals.add",
          color: "#64ba5f",
          name: "add",
          icon: "add",
          handler: () => {
            this.plan = item;
            this.dialogs.assign = true;
          },
        },
      ];
    },
    trans(item, field) {
      return translate2(item, field);
    },
    async update() {
      this.saving = true;
      try {
        await this.updatePlanFeeGuideProvince({
          payload: {
            PlanId: this.plan.PlanId,
            FeeGuideProvince: this.plan.ClaimSettings.FeeGuideProvince,
          },
        });
        this.snackbar = true;
        this.$refs.table.getDataAsync();
        this.dialogs.edit = false;
        this.plan = null;
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },
    onSuccess() {
      this.snackbar = true;
      this.dialogs.assign = false;
      this.$refs.table.getDataAsync();
    },
    async tryDelete() {
      this.saving = true;
      try {
        await this.deletePlanFeeGuide({
          id: this.item.Id,
        });
        this.snackbar = true;
        this.dialogs.delete = false;
        this.$refs.table.getDataAsync();
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
